import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Text, TextArea, TextInput} from "react-vcomponents";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {RunInAction_Set, TextPlus} from "web-vcore";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {GetAudioFilesWithSubpath, GetCache} from "../../../../Store/main/general/CacheState.js";
import {E, GetEntries} from "js-vextensions";
import {NarrateText, NarrateTextOptions, NarrateTextOptionsInit} from "../../../../Utils/Services/TTS.js";
import {ShowToast} from "../../../../Utils/Bridge/Bridge_Native/General.js";
import {InAndroid} from "../../../../Utils/Bridge/Bridge_Native.js";
import {PoolEntrySelectionType} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_MemoryPalace.js";
import {GetStandardFilePathForTTSOptions} from "../../../../Utils/Services/FilePaths.js";

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.memoryPalace, (c, v)=>c.memoryPalace = v);
type SharedProps = ReturnType<typeof GetSharedProps>;

const splitAt = 150;

export const MemoryPalace_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {client, enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5}}>
			<Row style={{fontSize: 18}}>
				<CheckBox text="Memory palace" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
			</Row>

			{config.enabled &&
			<>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Next prompt:</Text>
					<ControlInput enabled={enabled} value={config.nextPrompt_triggerSet!} onChange={val=>ChangeConfig(c=>c.nextPrompt_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Previous prompt:</Text>
					<ControlInput enabled={enabled} value={config.prevPrompt_triggerSet!} onChange={val=>ChangeConfig(c=>c.prevPrompt_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Voice, volume multiplier:</Text>
					<SpinnerForPercent enabled={enabled} min={0} max={999} value={config.voice_volumeMultiplier} onChange={val=>ChangeConfig(c=>c.voice_volumeMultiplier = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`Note: A "move" prompt cannot happen twice in a row. Also, only happens during day sessions atm.`}>Prompt, chance for move: (day)</TextPlus>
					<SpinnerForPercent enabled={enabled} value={config.prompt_chanceForMove} onChange={val=>ChangeConfig(c=>c.prompt_chanceForMove = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`Note: Insert the target's name using $target. Example: "Move the $target"`}>Phrase for move:</TextPlus>
					<TextInput enabled={enabled} value={config.prompt_movePhrase} onChange={val=>ChangeConfig(c=>c.prompt_movePhrase = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Target pool:</Text>
					<TextArea enabled={enabled} autoSize={true} style={{flex: 1}} value={config.targetPool?.join(", ") ?? ""} onChange={val=>{
						ChangeConfig(c=>c.targetPool = val.SplitByAny(",", "\n").map(a=>a.trim()).filter(a=>a.length > 0))
					}}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>TTS baking:</Text>
					<TTSBakingStatus targetPool={config.targetPool ?? []} enabled={enabled}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Pool entry selection type:</Text>
					<Select enabled={enabled} displayType="button bar" options={GetEntries(PoolEntrySelectionType, "ui")}
						value={config.selectionType} onChange={val=>ChangeConfig(c=>c.selectionType = val)}/>
				</RowLR>
			</>}
		</Column>
	);
});

const TTSBakingStatus = observer_mgl((props: {targetPool: string[], enabled?: boolean})=>{
	const {targetPool, enabled} = props;
	const [bakingProgress, setBakingProgress] = React.useState({current: 0, total: 0});
	const isBaking = bakingProgress.total > 0;

	const unbaked = targetPool.filter(target=>{
		const baked = GetAudioFilesWithSubpath("Alarms/LF/BakedTTS/").find(a=>a.filePath.includes(`text(${target})`));
		return !baked;
	});

	return (
		<Row style={{flexWrap: "wrap", columnGap: 5, rowGap: 3}}>
			{targetPool.map((target, index)=>{
				const baked = GetCache().audioFiles.find(a=>a.filePath.includes("BakedTTS") && a.filePath.includes(`text(${target})`));
				return <Text key={index} style={E(
					!baked && {color: "red"},
					baked && {color: "lightgreen"},
				)}>{target}</Text>
			})}
			<Button text={isBaking ? `Baking ${bakingProgress.current}/${bakingProgress.total}` : "Bake all"} enabled={enabled && unbaked.length > 0 && !isBaking && InAndroid(0)} onClick={async()=>{
				setBakingProgress({current: 0, total: unbaked.length});
				try {
					for (let i = 0; i < unbaked.length; i++) {
						const target = unbaked[i];
						setBakingProgress({current: i + 1, total: unbaked.length});
						await NarrateText({
							text: target,
							finalize_lastStep: self=>{
								self.saveToFile_relFilePath = GetStandardFilePathForTTSOptions(self.ExcludeKeys("finalize_lastStep"));
							},
						});
					}
					ShowToast(`Baked ${unbaked.length} TTS messages.`);
					RunInAction_Set(()=>GetCache().audioFiles_updatedRequestedAt = Date.now());
				} finally {
					setBakingProgress({current: 0, total: 0});
				}
			}}/>
			<Button ml={5} mdIcon="refresh" title="Refresh bake statuses" enabled={enabled && !isBaking && InAndroid(0)} onClick={()=>{
				RunInAction_Set(()=>GetCache().audioFiles_updatedRequestedAt = Date.now());
			}}/>
		</Row>
	);
});